import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import api from '../lib/api';
import { toast } from 'sonner';
import { Users, MapPin, Heart, Copy, Plus, Footprints, Crown, Trash2, UserPlus, X, Send, Mountain, Percent } from 'lucide-react';

function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Link copied!');
    }).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

function fallbackCopy(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
    toast.success('Link copied!');
  } catch {
    toast.error('Copy manually: ' + text);
  }
  document.body.removeChild(textarea);
}

export default function TeamPage() {
  const { user, fetchUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', tagline: '' });
  const [browseTeams, setBrowseTeams] = useState(null);
  const [browseQuery, setBrowseQuery] = useState('');
  const [joining, setJoining] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [newInvites, setNewInvites] = useState([{ name: '', email: '' }]);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);

  const loadTeam = () => {
    api.get('/teams/my')
      .then(r => setTeam(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTeam(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Team name required'); return; }
    setCreating(true);
    try {
      await api.post('/teams', { name: form.name.trim(), tagline: form.tagline.trim() });
      await fetchUser();
      toast.success('Team created!');
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  // Load active teams on the walker's challenge so they can browse and join one.
  // We append a `_=Date.now()` cache buster so the browser never serves a stale
  // list from its HTTP cache (e.g. if a friend just created a team).
  const reloadBrowseTeams = useCallback(() => {
    if (!user?.challenge_id) { setBrowseTeams([]); return; }
    setBrowseTeams(null);
    api.get(`/teams/search?challenge_id=${user.challenge_id}&_=${Date.now()}`)
      .then(r => setBrowseTeams(r.data || []))
      .catch(() => setBrowseTeams([]));
  }, [user?.challenge_id]);

  useEffect(() => {
    if (team) return; // already on a team — no need to browse
    reloadBrowseTeams();
  }, [team, reloadBrowseTeams]);

  const filteredBrowse = (browseTeams || []).filter(t => {
    if (!browseQuery.trim()) return true;
    const q = browseQuery.toLowerCase();
    return (t.name || '').toLowerCase().includes(q) || (t.tagline || '').toLowerCase().includes(q);
  });

  const handleJoinTeam = async (teamToJoin) => {
    if (!teamToJoin?.invite_code) { toast.error('Cannot join this team — missing invite code.'); return; }
    setJoining(teamToJoin.id);
    try {
      await api.post(`/teams/join/${teamToJoin.invite_code}`);
      await fetchUser();
      toast.success(`Joined ${teamToJoin.name}!`);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join team');
    } finally {
      setJoining(null);
    }
  };

  const copyInvite = () => {
    if (team?.invite_code) {
      const url = `${window.location.origin}/teams/join/${team.invite_code}`;
      copyToClipboard(url);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!confirm(`Are you sure you want to remove ${memberName} from the team?`)) return;
    setRemovingMember(memberId);
    try {
      await api.delete(`/teams/members/${memberId}`);
      toast.success(`${memberName} has been removed from the team`);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleSendInvites = async () => {
    const valid = newInvites.filter(i => i.name.trim() && i.email.trim());
    if (valid.length === 0) {
      toast.error('Please add at least one name and email');
      return;
    }
    setSendingInvites(true);
    try {
      for (const inv of valid) {
        await api.post('/teams/invite-teammate', { name: inv.name, email: inv.email });
      }
      toast.success(`${valid.length} teammate invite${valid.length > 1 ? 's' : ''} sent!`);
      setNewInvites([{ name: '', email: '' }]);
      setShowInviteForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSendingInvites(false);
    }
  };

  const isLeader = team && user && team.creator_id === user.id;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="container-app py-8 md:py-12" data-testid="team-page">
      <h1 className="text-2xl md:text-3xl font-bold text-stone-900 mb-8">Your Team</h1>

      {!team ? (
        /* Join (left, primary) + Create (right, secondary) */
        <div className="max-w-6xl mx-auto" data-testid="no-team-section">
          <div className="grid lg:grid-cols-3 gap-6 items-start">

            {/* JOIN — left, 2/3 width, search + scrollable list inline */}
            <Card className="lg:col-span-2 bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="join-team-section">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-orange-500" />
                      Join a Team
                    </h2>
                    <p className="text-sm text-stone-500 mt-0.5">Pick from active teams on your challenge.</p>
                  </div>
                  {browseTeams !== null && (
                    <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-orange-100 text-orange-700 text-xs font-bold px-2.5 py-1" data-testid="browse-teams-count-badge">
                      {browseQuery
                        ? `${filteredBrowse.length} of ${browseTeams.length}`
                        : `${browseTeams.length} total`}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <Input
                    value={browseQuery}
                    onChange={(e) => setBrowseQuery(e.target.value)}
                    placeholder="Search teams by name or tagline…"
                    className="rounded-xl border-stone-200 bg-stone-50 h-11 flex-1"
                    data-testid="team-browse-search"
                  />
                  <button
                    type="button"
                    onClick={reloadBrowseTeams}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium underline-offset-2 hover:underline shrink-0 px-2"
                    data-testid="browse-teams-refresh"
                  >
                    Refresh
                  </button>
                </div>

                {browseTeams === null ? (
                  <p className="text-center text-sm text-stone-400 py-12">Loading teams…</p>
                ) : browseTeams.length === 0 ? (
                  <p className="text-center text-sm text-stone-400 py-12">No active teams on your challenge yet — be the first to create one!</p>
                ) : filteredBrowse.length === 0 ? (
                  <p className="text-center text-sm text-stone-400 py-12">No teams match "{browseQuery}".</p>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                    {filteredBrowse.map(t => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between gap-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition p-3 flex-wrap"
                        data-testid={`browse-team-${t.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-stone-900 truncate">{t.name}</p>
                          {t.tagline && <p className="text-xs text-stone-500 italic truncate">"{t.tagline}"</p>}
                          <p className="text-[11px] text-stone-400 mt-0.5">
                            Leader: {t.leader?.display_name || t.leader?.full_name || '—'}
                            {' · '}
                            {Math.max(0, (t.members_count || 1) - 1)} teammate{Math.max(0, (t.members_count || 1) - 1) === 1 ? '' : 's'} ({t.members_count || 1} total)
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleJoinTeam(t)}
                          disabled={joining === t.id}
                          className="rounded-full bg-orange-600 hover:bg-orange-700 text-white px-5"
                          data-testid={`browse-team-join-${t.id}`}
                        >
                          <UserPlus className="w-3.5 h-3.5 mr-1" />
                          {joining === t.id ? 'Joining…' : 'Join Team'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CREATE — right column, sticky / compact */}
            <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] lg:sticky lg:top-6" data-testid="create-team-section">
              <CardContent className="p-6">
                <h2 className="text-base font-bold text-stone-900 flex items-center gap-2 mb-1">
                  <Plus className="w-4 h-4 text-orange-500" />
                  Create a Team
                </h2>
                <p className="text-xs text-stone-500 mb-4">Don't see the right team? Start your own.</p>
                <form onSubmit={handleCreate} className="space-y-3 text-left">
                  <div>
                    <Label className="text-stone-700 text-xs font-medium">Team Name</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm(f => ({...f, name: e.target.value}))}
                      placeholder="e.g. KEF Trailblazers"
                      required
                      className="mt-1 rounded-xl border-stone-200 bg-stone-50 h-10"
                      data-testid="team-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-700 text-xs font-medium">Tagline</Label>
                    <Input
                      value={form.tagline}
                      onChange={(e) => setForm(f => ({...f, tagline: e.target.value}))}
                      placeholder="e.g. Step by step, mile by mile"
                      className="mt-1 rounded-xl border-stone-200 bg-stone-50 h-10"
                      data-testid="team-tagline-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="rounded-full bg-orange-600 hover:bg-orange-700 text-white font-medium w-full"
                    data-testid="team-create-btn"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    {creating ? 'Creating...' : 'Create Team'}
                  </Button>
                </form>
              </CardContent>
            </Card>

          </div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="team-management-section">
          {/* Team Header with Leader */}
          <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-stone-900">{team.name}</h2>
                    {isLeader && (
                      <Badge className="bg-amber-100 text-amber-700 rounded-full text-xs">
                        <Crown className="w-3 h-3 mr-1" /> Team Leader
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-stone-500">{team.description}</p>
                  {team.leader && (
                    <p className="text-xs text-stone-400 mt-2">
                      Led by <span className="font-medium text-stone-600">{team.leader.display_name || team.leader.full_name}</span>
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invite Section */}
          <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <div className="text-center mb-5">
                <UserPlus className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <h3 className="text-base font-bold text-stone-900">Invite friends, family and colleagues to join your Team</h3>
              </div>

              {/* Copy invite link */}
              <div className="flex gap-2 mb-5 max-w-md mx-auto">
                <Input
                  readOnly
                  value={team?.invite_code ? `${window.location.origin}/teams/join/${team.invite_code}` : ''}
                  className="rounded-xl bg-stone-50 border-stone-200 text-xs h-10 text-stone-600"
                  data-testid="team-invite-link"
                />
                <Button onClick={copyInvite} className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white shrink-0" data-testid="team-copy-invite-btn">
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
              </div>

              {/* Invite Teammates by Email */}
              <div className="border-t border-stone-100 pt-5">
                <h4 className="text-sm font-bold text-stone-900 mb-3">Invite Teammates</h4>
                <div className="space-y-2 mb-4">
                  {newInvites.map((inv, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input
                        value={inv.name}
                        onChange={(e) => {
                          const copy = [...newInvites];
                          copy[i].name = e.target.value;
                          setNewInvites(copy);
                        }}
                        placeholder="Name"
                        className="rounded-xl border-stone-200 bg-stone-50 h-10 flex-1"
                        data-testid={`team-invite-name-${i}`}
                      />
                      <Input
                        type="email"
                        value={inv.email}
                        onChange={(e) => {
                          const copy = [...newInvites];
                          copy[i].email = e.target.value;
                          setNewInvites(copy);
                        }}
                        placeholder="Email"
                        className="rounded-xl border-stone-200 bg-stone-50 h-10 flex-1"
                        data-testid={`team-invite-email-${i}`}
                      />
                      {newInvites.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setNewInvites(newInvites.filter((_, j) => j !== i))}
                          className="text-stone-300 hover:text-red-500 shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setNewInvites([...newInvites, { name: '', email: '' }])}
                    className="rounded-xl border-stone-200 text-stone-600"
                    data-testid="team-add-invite-row"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add More
                  </Button>
                  <Button
                    onClick={handleSendInvites}
                    disabled={sendingInvites}
                    className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="team-send-invites"
                  >
                    <Send className="w-4 h-4 mr-1" /> {sendingInvites ? 'Sending...' : 'Send Invites'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="p-5">
                <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center mb-3">
                  <MapPin className="w-4 h-4 text-orange-600" />
                </div>
                <p className="text-2xl font-bold text-stone-900">{team.total_km} km</p>
                <p className="text-xs text-stone-400 uppercase tracking-wider font-medium mt-0.5">Total Distance</p>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="p-5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center mb-3">
                  <Heart className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-2xl font-bold text-stone-900">${team.total_raised}</p>
                <p className="text-xs text-stone-400 uppercase tracking-wider font-medium mt-0.5">Total Raised</p>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="p-5">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center mb-3">
                  <Percent className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-stone-900">{team.avg_progress_pct || 0}%</p>
                <p className="text-xs text-stone-400 uppercase tracking-wider font-medium mt-0.5">Avg Completion</p>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="p-5">
                <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center mb-3">
                  <Users className="w-4 h-4 text-sky-600" />
                </div>
                <p className="text-2xl font-bold text-stone-900">{team.members?.length || 0}</p>
                <p className="text-xs text-stone-400 uppercase tracking-wider font-medium mt-0.5">Members</p>
              </CardContent>
            </Card>
          </div>

          {/* Members Table */}
          <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-stone-900 mb-4">Team Members</h3>
              {/* Table header */}
              <div className="hidden sm:grid grid-cols-12 gap-2 px-4 pb-2 border-b border-stone-100 text-xs text-stone-400 font-medium uppercase tracking-wider">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Challenge</div>
                <div className="col-span-3">Progress</div>
                <div className="col-span-2 text-right">Total Pledged</div>
                <div className="col-span-1"></div>
              </div>
              <div className="space-y-2 mt-2">
                {/* Sort: leader first */}
                {[...(team.members || [])].sort((a, b) => (b.is_leader ? 1 : 0) - (a.is_leader ? 1 : 0)).map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 rounded-xl bg-stone-50" data-testid={`team-member-${member.id}`}>
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-11 h-11 rounded-full bg-orange-100 flex items-center justify-center relative">
                        <span className="text-sm font-bold text-orange-700">{member.display_name?.[0] || member.full_name?.[0]}</span>
                        {member.is_leader && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center border-2 border-white">
                            <Crown className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-stone-900">{member.display_name || member.full_name}</p>
                          {member.is_leader && (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-full">Leader</Badge>
                          )}
                        </div>
                        {member.email && (
                          <a
                            href={`mailto:${member.email}`}
                            className="text-xs text-stone-500 hover:text-orange-600 underline-offset-2 hover:underline break-all"
                            data-testid={`team-member-email-${member.id}`}
                          >
                            {member.email}
                          </a>
                        )}
                        {member.challenge && (
                          <p className="text-xs text-stone-400 flex items-center gap-1 mt-0.5">
                            <Mountain className="w-3 h-3" /> {member.challenge.name}
                          </p>
                        )}
                        <div className="mt-2 max-w-xs">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-stone-500">{member.total_km} km</span>
                            <span className="font-medium text-stone-700">{member.progress_pct}%</span>
                          </div>
                          <Progress value={member.progress_pct} className="h-1.5" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-bold text-emerald-600">${member.total_raised}</p>
                        <p className="text-xs text-stone-400">raised</p>
                      </div>
                      {isLeader && !member.is_leader && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveMember(member.id, member.display_name || member.full_name)}
                          disabled={removingMember === member.id}
                          className="text-stone-300 hover:text-red-500 hover:bg-red-50"
                          data-testid={`remove-member-${member.id}`}
                        >
                          {removingMember === member.id ? (
                            <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
